import type React from "react";
import {
  type JSXElementConstructor,
  type ReactElement,
  cloneElement,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { Button } from "simplydo/react/ui/button";
import { H5, Subheader } from "simplydo/react/ui/header";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import Tag, {
  TagContainer,
  type TagLike,
  type TagProps,
} from "components/lib/Tag";

type TagViewProps = {
  tags: TagLike[];
  hideView?: boolean;
  noMargin?: boolean;
  children?: React.ReactNode;
} & Omit<TagProps, "tag">;

export const TagView = ({
  tags,
  hideView,
  noMargin,
  children,
  ...tagProps
}: TagViewProps) =>
  hideView ? (
    children
  ) : (
    <TagContainer $noMargin={noMargin}>
      {tags ? tags.map((t) => <Tag tag={t} key={t._id} {...tagProps} />) : null}
      {children}
    </TagContainer>
  );

type TagChooserContentProps<T extends TagLike> = {
  tags: TagLike[];
  allTags: T[];
  removeTag: (tag: TagLike) => void;
  addTag: (tag: T) => void;
  showCount: boolean;
};

export const TagChooserContent = <T extends TagLike>({
  tags,
  allTags,
  removeTag,
  addTag,
  showCount,
}: TagChooserContentProps<T>) => {
  const tagIds = useMemo(() => tags.map((c) => c._id), [tags]);
  const { t } = useTranslation();
  const { selectedTags = [], availableTags = [] } = (allTags || []).reduce<{
    selectedTags: T[];
    availableTags: T[];
  }>(
    (p, c) => {
      if (tagIds.includes(c._id)) {
        p.selectedTags.push(c);
      } else {
        p.availableTags.push(c);
      }
      return p;
    },
    { selectedTags: [], availableTags: [] },
  );
  return (
    <>
      {tags?.length > 0 && <H5>Selected tags</H5>}
      <TagView
        tags={selectedTags}
        showCount={showCount}
        noRedirect
        onClick={(t) => {
          removeTag(t);
        }}
        onRemove={(t) => {
          removeTag(t);
        }}
      />
      <H5>
        {t("generic.tags")}
        <Subheader>{t("tags.click")}</Subheader>
      </H5>
      <TagView
        tags={availableTags}
        noRedirect
        showCount={showCount}
        onClick={(t) => {
          const tag = availableTags.find((a) => a._id === t._id);
          if (tag && !tagIds.includes(tag._id)) {
            addTag(tag);
          }
        }}
      />
    </>
  );
};

type TagModalChooserProps<T extends TagLike> = {
  tags: TagLike[];
  availableTags?: T[];
  removeTag?: (tag: TagLike) => void;
  addTag?: (tag: T) => void;
  trigger?: ReactElement<any, string | JSXElementConstructor<any>>;
  noRedirect?: boolean;
  canEdit?: boolean;
  showCount?: boolean;
  viewCanRemove?: boolean;
  onStateChange?: (state: boolean) => void;
  hideView?: boolean;
  noMargin?: boolean;
  isChallenge?: boolean;

  isOpen?: boolean;
  onClose?: () => void;
};

const TagModalChooser = <T extends TagLike>({
  isOpen,
  onClose,
  tags = [],
  availableTags = [],
  removeTag,
  addTag,
  trigger,
  noRedirect,
  canEdit = true,
  showCount = true,
  viewCanRemove,
  onStateChange,
  hideView = false,
  noMargin,
  isChallenge,
}: TagModalChooserProps<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(isOpen ?? false);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(!!isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isOpen === undefined) {
      setOpen(false);
    }
    onClose?.();
  };

  const triggerElement = trigger
    ? cloneElement(trigger, {
        onClick: () => {
          onStateChange?.(true);
          setOpen(!open);
        },
      })
    : null;

  const allTags = availableTags || [];

  if (!canEdit) {
    return (
      <TagView
        tags={tags}
        hideView={hideView}
        noMargin={noMargin}
        noRedirect={noRedirect}
      />
    );
  }

  if (availableTags.length === 0) {
    return null;
  }
  return (
    <>
      <TagView
        tags={tags}
        noRedirect={noRedirect}
        onRemove={viewCanRemove ? removeTag : undefined}
        hideView={hideView}
        noMargin={noMargin}
      >
        {triggerElement}
      </TagView>

      <Modal
        open={open}
        closeOnDimmerClick
        onClose={() => {
          onStateChange?.(false);
          handleClose();
        }}
      >
        <ModalHeader>{t("generic.tags")}</ModalHeader>
        <ModalContent>
          <p>
            {t("tags.select")}{" "}
            {isChallenge ? t("generic.challenge") : `${t("generic.idea")}`}.
          </p>
          <TagChooserContent
            tags={tags}
            allTags={allTags}
            removeTag={removeTag ?? (() => {})}
            addTag={addTag ?? (() => {})}
            showCount={showCount}
          />
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              onStateChange?.(false);
              handleClose();
            }}
            color="primary"
          >
            {t("generic.done")}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default TagModalChooser;
